import { CustomScrollbarTextarea, InputGroup, isEnterKey } from "@smartsuite/react-ui/lib";
import type { PlainTextFieldProps } from "@smartsuite/react-ui/lib/components/forms/PlainTextField/PlainTextField";
import cx from "classnames";
import type React from "react";
import { FormFieldControl } from "../../../../components/FormFieldControl/FormFieldControl";
import { useFormField } from "../../../../hooks/useFormField";
import type { FormFieldProps } from "../../../types";
import type { TextFieldValue } from "../text.config";
import { validateByType } from "../validation";
import "./TextFieldControl.sass";
import { TextAccelerator } from "../../../../components/TextAccelerator/TextAccelerator";
export type TextFieldControlProps = FormFieldProps &
  Pick<PlainTextFieldProps, "placeholder" | "type">;

const DEFAULT_MAX_LENGTH = 500;

export const TextFieldControl: React.FunctionComponent<TextFieldControlProps> = ({
  caption,
  className,
  field,
  formItem,
  label,
  name,
  placeholder,
  required,
  type = "text",
  readOnly,
}) => {
  const formField = useFormField<TextFieldValue>({
    name,
    validate: validateByType(type, field, formItem),
  });

  const maxLength = field.params.max_length;

  // Prevents attempted form submission on enter. It also prevents
  // a previously edited dropdown field to have its value cleaned.
  // TODO: Need to investigate why we are keeping a reference to those
  // fields and clearing them when editing these here
  const handleKeyDown = (event: KeyboardEvent): void => {
    if (isEnterKey(event)) event.preventDefault();
  };

  const inputClassName = cx("form-text-field", className, {
    "has-value": !!formField.value,
  });

  return (
    <FormFieldControl
      captionAsTooltip={formItem.params.help_text_as_tooltip}
      caption={caption}
      helpText={formItem.params.helpText}
      errorMessage={formField.errorMessage}
      label={label}
      required={required}
      state={formField.state}
      readOnly={readOnly}
    >
      <InputGroup state={formField.state} inputBackground="white-new">
        <CustomScrollbarTextarea
          changeHeightWhenTyping
          className={inputClassName}
          minHeight={32}
          maxHeight={125}
          maxLength={maxLength ?? DEFAULT_MAX_LENGTH}
          name={formField.name}
          placeholder={placeholder}
          required={required}
          readOnly={readOnly}
          value={formField.value}
          onBlur={formField.onBlur}
          onChange={formField.onChange}
          onFocus={formField.onFocus}
          onKeyDown={(e) => handleKeyDown(e as unknown as KeyboardEvent)}
        />
        <TextAccelerator maxLength={maxLength} length={formField.value?.length ?? 0} />
      </InputGroup>
    </FormFieldControl>
  );
};
